/** Vertical offset for pane label (mb-2 + text-xs line) in chart container coords. */
export const LABOR_CHART_PANE_LABEL_HEIGHT = 24

export const LABOR_TRACKING_LINE_DASH = '4 3'

export function laborPlotYScale(
  value: number,
  innerHeight: number,
  yMin: number,
  yMax: number,
): number {
  const yRange = yMax - yMin || 1
  return innerHeight - ((value - yMin) / yRange) * innerHeight
}

export function invertLaborPlotY(
  yInner: number,
  innerHeight: number,
  yMin: number,
  yMax: number,
): number {
  const yRange = yMax - yMin || 1
  return yMin + ((innerHeight - yInner) / innerHeight) * yRange
}

export interface NearestSeriesPath {
  key: string
  values: Array<{ value: number | null }>
}

export function nearestSeriesKeyAtDate(
  visiblePaths: NearestSeriesPath[],
  dateIndex: number,
  yInner: number,
  yScale: (value: number) => number,
): string | null {
  if (visiblePaths.length === 0) return null
  let nearestKey: string | null = null
  let nearestDist = Number.POSITIVE_INFINITY
  for (const line of visiblePaths) {
    const value = line.values[dateIndex]?.value
    if (value == null) continue
    const dist = Math.abs(yScale(value) - yInner)
    if (dist < nearestDist) {
      nearestDist = dist
      nearestKey = line.key
    }
  }
  return nearestKey
}

export const LABOR_TOOLTIP_OFFSET = 12
export const LABOR_TOOLTIP_PADDING = 8

export function fitLaborTooltipInContainer({
  preferredLeft,
  preferredTop,
  tooltipWidth,
  tooltipHeight,
  containerWidth,
  containerHeight,
  cursorX,
  offsetX = LABOR_TOOLTIP_OFFSET,
  padding = LABOR_TOOLTIP_PADDING,
}: {
  preferredLeft: number
  preferredTop: number
  tooltipWidth: number
  tooltipHeight: number
  containerWidth: number
  containerHeight: number
  cursorX?: number
  offsetX?: number
  padding?: number
}): { left: number; top: number } {
  let left = preferredLeft
  let top = preferredTop

  if (left + tooltipWidth + padding > containerWidth) {
    const flipAnchor = cursorX ?? preferredLeft - offsetX
    left = flipAnchor - tooltipWidth - offsetX
  }

  const maxLeft = Math.max(padding, containerWidth - tooltipWidth - padding)
  left = Math.min(Math.max(left, padding), maxLeft)

  const maxTop = Math.max(padding, containerHeight - tooltipHeight - padding)
  top = Math.min(Math.max(top, padding), maxTop)

  return { left, top }
}

export function peerTooltipPosition({
  dateIndex,
  xStep,
  marginLeft,
  marginTop,
  paneLabelHeight = LABOR_CHART_PANE_LABEL_HEIGHT,
  highlightYInner,
  offsetX = 12,
  offsetY = 12,
}: {
  dateIndex: number
  xStep: number
  marginLeft: number
  marginTop: number
  paneLabelHeight?: number
  highlightYInner?: number | null
  offsetX?: number
  offsetY?: number
}): { left: number; top: number } {
  const plotX = marginLeft + dateIndex * xStep
  const yInner = highlightYInner ?? 0
  return {
    left: plotX + offsetX,
    top: paneLabelHeight + marginTop + yInner + offsetY,
  }
}
